import { z } from 'zod';

// HTTP methods
export const httpMethodSchema = z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']);
export type HttpMethod = z.infer<typeof httpMethodSchema>;

// JSON Schema parameter type enum
export const jsonSchemaTypeSchema = z.enum([
  'string',
  'number',
  'integer',
  'boolean',
  'array',
  'object',
]);
export type JsonSchemaType = z.infer<typeof jsonSchemaTypeSchema>;

// Parameter location enum. `display` is never sent to the API: when the AI sets
// it to true, the tool result goes back to the AI but is not shown to the customer.
export const parameterInSchema = z.enum(['path', 'query', 'body', 'filter', 'display']);
export type ParameterIn = z.infer<typeof parameterInSchema>;

export const filterOperatorSchema = z.enum([
  '>=',
  '<=',
  '>',
  '<',
  '==',
  '!=',
  'contains',
  'starts_with',
]);
export type FilterOperator = z.infer<typeof filterOperatorSchema>;

export const filterConfigSchema = z.object({
  target_field: z.string().min(1, 'Target field is required'),
  operator: filterOperatorSchema,
});
export type FilterConfig = z.infer<typeof filterConfigSchema>;

// JSON Schema property definition for tool parameters
export const jsonSchemaPropertySchema: z.ZodType<any> = z.lazy(() =>
  z.object({
    type: jsonSchemaTypeSchema,
    description: z.string().optional(),
    nullable: z.boolean().optional(),
    enum: z.array(z.union([z.string(), z.number()])).optional(),
    items: z
      .object({
        type: jsonSchemaTypeSchema,
        description: z.string().optional(),
        nullable: z.boolean().optional(),
        enum: z.array(z.union([z.string(), z.number()])).optional(),
        properties: z.record(z.string(), jsonSchemaPropertySchema).optional(),
        required: z.array(z.string()).optional(), // Support required fields for array item properties
      })
      .optional(),
    properties: z.record(z.string(), jsonSchemaPropertySchema).optional(),
    required: z.array(z.string()).optional(), // Support required fields for nested object properties
    default: z.any().optional(),
    minimum: z.number().optional(),
    maximum: z.number().optional(),
    minLength: z.number().optional(),
    maxLength: z.number().optional(),
    pattern: z.string().optional(),
    fixed_value: z.any().optional(), // Fixed value that bypasses AI generation
    in: parameterInSchema.optional(), // Where the parameter goes: path, query, or body
    path_variable: z.string().optional(), // Name of the path variable this parameter maps to (e.g., 'dealer_id' for /dealer/{dealer_id})
    query_param: z.string().optional(), // Real query-string name when it differs from the property key (e.g. property 'max_price' -> 'max-price'). Lets a valid-identifier property map to a kebab-case API param.
    filter_config: filterConfigSchema.optional(),
  }),
);

export type JsonSchemaProperty = z.infer<typeof jsonSchemaPropertySchema>;

// Parameters schema (JSON Schema format)
export const parametersSchemaDefinition = z.object({
  type: z.literal('object'),
  properties: z.record(z.string(), jsonSchemaPropertySchema),
  required: z.array(z.string()).optional().default([]),
  body_template: z.unknown().optional(),
});

export type ParametersSchemaDefinition = z.infer<typeof parametersSchemaDefinition>;

// A selected field is either a plain dot-notation path (string), or a computed
// field: extract `path`, optionally take a `substr` (String.slice(start, end))
// of the string value and/or `add` a number to it, then output it under `as`
// (defaults to the last path segment). Lets a response build derived values —
// e.g. an id prefix for an image URL, or a price plus a fixed admin fee —
// without any per-tool code.
export const selectedFieldSchema = z.union([
  z.string(),
  z.object({
    path: z.string().min(1),
    as: z.string().min(1).optional(),
    substr: z.tuple([z.number(), z.number()]).optional(),
    // Numeric offset added to the extracted value (may be negative). The value
    // is coerced to a number; if it isn't numeric the original value is kept.
    add: z.number().optional(),
  }),
]);

export type SelectedField = z.infer<typeof selectedFieldSchema>;

// Response mapping schema
export const responseMappingSchema = z.object({
  success_path: z.string().optional(),
  output_path: z.string().optional(),
  error_path: z.string().optional(),
  // Dot-notation paths to extract (e.g., ["data.id", "data.name"]), or computed
  // fields ({ path, as?, substr? }) for derived values.
  selected_fields: z.array(selectedFieldSchema).optional(),
});

export type ResponseMapping = z.infer<typeof responseMappingSchema>;

// ── Display config for widget rendering ──────────────────────────

export const displayTypeSchema = z.enum(['carousel', 'cards', 'notice', 'badges', 'links']);

export const displayFieldsSchema = z.object({
  image: z.string().optional(),
  title: z.string(),
  description: z.string().optional(),
  price: z.string().optional(),
  link: z.string().optional(),
  badge: z.string().optional(),
});

export const displayConfigSchema = z
  .object({
    type: displayTypeSchema,
    array_path: z.string().optional(),
    fields: displayFieldsSchema,
    // When true, the rendered result is pinned in the widget overlay: it stays
    // on screen when later tool results appear, and new results stack beneath it
    // instead of replacing it.
    fixed: z.boolean().optional(),
    // Image URL used when an item's `image` can't be fully resolved (e.g. a
    // vehicle with no photo). Per-config so each client sets their own
    // placeholder; must be a public JPG/PNG for WhatsApp carousels.
    fallback_image: z.string().optional(),
    whatsapp_link_base: z.string().optional(),
  })
  .refine(
    (data) =>
      data.type === 'notice' ||
      data.type === 'links' ||
      data.type === 'badges' ||
      (data.array_path && data.array_path.length > 0),
    { message: 'array_path is required for carousel and cards display types' },
  );

export type DisplayConfig = z.infer<typeof displayConfigSchema>;
export type DisplayType = z.infer<typeof displayTypeSchema>;
export type DisplayFields = z.infer<typeof displayFieldsSchema>;

// ── Two-step bearer-token auth ───────────────────────────────────
// When set, the executor fetches a short-lived token from an auth endpoint,
// caches it until it expires, and injects it into every outgoing request.
// Used for APIs whose access token is issued separately and expires (e.g.
// AutoHQ: GET /v1/key/request-token with an X-Secret-Key -> 20-min bearer).
export const tokenAuthSchema = z.object({
  // Auth endpoint that issues the token. {{env.NAME}} placeholders allowed.
  token_url: z.string().min(1, 'Token URL is required'),
  // HTTP method for the token request (defaults to GET).
  token_method: httpMethodSchema.optional(),
  // Headers sent to the token endpoint. {{env.NAME}} placeholders are resolved
  // from the server environment so the long-lived secret never sits in the DB.
  token_headers: z.record(z.string(), z.string()).optional(),
  // Optional query params / body for the token request.
  token_query: z.record(z.string(), z.string()).optional(),
  token_body: z.unknown().optional(),
  // Dot-path to the token string in the auth response (defaults to "token").
  token_path: z.string().optional(),
  // Dot-path to the token lifetime in seconds (e.g. "expiresIn"). Used to cache
  // the token and refresh it shortly before it expires.
  expires_in_path: z.string().optional(),
  // Header the token is injected into on the main call (defaults to
  // "Authorization") and how it is formatted (defaults to "Bearer {{token}}").
  inject_header: z.string().optional(),
  inject_template: z.string().optional(),
  // Response statuses that trigger a one-time token refresh + retry
  // (defaults to [401, 403]).
  refresh_status_codes: z.array(z.number()).optional(),
});

export type TokenAuth = z.infer<typeof tokenAuthSchema>;

// Engine-level invocation guard. 'per_call': a second invocation in the same
// call returns the cached first result without re-running the webhook.
// 'per_call_and_args': same, scoped to (call, arguments) — identical args are
// served from cache, different args run. 'none' (default): every call runs.
export const toolIdempotencySchema = z.enum(['none', 'per_call', 'per_call_and_args']);
export type ToolIdempotency = z.infer<typeof toolIdempotencySchema>;

// Custom tool base schema
export const customToolSchema = z.object({
  id: z.string().uuid(),
  organization_id: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  http_method: httpMethodSchema,
  url: z.string().url(),
  headers: z.record(z.string(), z.string()).optional().default({}),
  parameters_schema: parametersSchemaDefinition,
  response_mapping: responseMappingSchema.optional(),
  is_active: z.boolean(),
  is_external: z.boolean().optional().default(false),
  // When true, invocations of this tool trigger an in-platform notification and
  // highlight the related contact on the contacts page.
  watch_enabled: z.boolean().optional().default(false),
  // When set, this tool runs the referenced scrapers config (HTML→JSON) instead
  // of a plain HTTP request. url/http_method remain present but act as placeholders.
  scraper_id: z.string().uuid().nullable().optional(),
  // Optional two-step bearer-token auth (see tokenAuthSchema).
  token_auth: tokenAuthSchema.nullable().optional(),
  idempotency: toolIdempotencySchema.optional().default('none'),
  created_at: z.date().or(z.string().datetime()),
  updated_at: z.date().or(z.string().datetime()),
});

export type CustomTool = z.infer<typeof customToolSchema>;

// Create custom tool DTO
export const createCustomToolSchema = z.object({
  organization_id: z.string().uuid('Invalid organization ID').optional(),
  name: z
    .string()
    .min(2, 'Tool name must be at least 2 characters')
    .max(100, 'Tool name must be at most 100 characters')
    .regex(
      /^[a-z_][a-z0-9_]*$/,
      'Tool name must be snake_case (lowercase letters, numbers, and underscores only)',
    ),
  description: z.string().min(10, 'Description must be at least 10 characters'),
  http_method: httpMethodSchema,
  url: z.string().url('Invalid URL format'),
  headers: z.record(z.string(), z.string()).optional(),
  parameters_schema: parametersSchemaDefinition,
  response_mapping: responseMappingSchema.optional(),
  is_active: z.boolean().optional().default(true),
  is_external: z.boolean().optional().default(false),
  watch_enabled: z.boolean().optional(),
  display_config: displayConfigSchema.nullable().optional(),
  whatsapp_template_name: z.string().max(512).nullable().optional(),
  scraper_id: z.string().uuid('Invalid scraper id').nullable().optional(),
  token_auth: tokenAuthSchema.nullable().optional(),
  idempotency: toolIdempotencySchema.optional(),
});

export type CreateCustomToolDto = z.infer<typeof createCustomToolSchema>;

// Update custom tool DTO
export const updateCustomToolSchema = z.object({
  name: z
    .string()
    .min(2, 'Tool name must be at least 2 characters')
    .max(100, 'Tool name must be at most 100 characters')
    .regex(
      /^[a-z_][a-z0-9_]*$/,
      'Tool name must be snake_case (lowercase letters, numbers, and underscores only)',
    )
    .optional(),
  description: z.string().min(10, 'Description must be at least 10 characters').optional(),
  http_method: httpMethodSchema.optional(),
  url: z.string().url('Invalid URL format').optional(),
  headers: z.record(z.string(), z.string()).optional(),
  parameters_schema: parametersSchemaDefinition.optional(),
  response_mapping: responseMappingSchema.optional(),
  is_active: z.boolean().optional(),
  is_external: z.boolean().optional(),
  watch_enabled: z.boolean().optional(),
  display_config: displayConfigSchema.nullable().optional(),
  whatsapp_template_name: z.string().max(512).nullable().optional(),
  scraper_id: z.string().uuid('Invalid scraper id').nullable().optional(),
  token_auth: tokenAuthSchema.nullable().optional(),
  idempotency: toolIdempotencySchema.optional(),
});

export type UpdateCustomToolDto = z.infer<typeof updateCustomToolSchema>;

// Query custom tools DTO
export const customToolQuerySchema = z.object({
  page: z.coerce.number().int().min(1).optional().default(1),
  limit: z.coerce.number().int().min(1).max(100).optional().default(10),
  search: z.string().optional(),
  organization_id: z.string().uuid().optional(),
  is_active: z.coerce.boolean().optional(),
  http_method: httpMethodSchema.optional(),
  sortBy: z.enum(['created_at', 'updated_at', 'name']).optional().default('created_at'),
  sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
});

export type CustomToolQuery = z.infer<typeof customToolQuerySchema>;

// Custom tool detail (for responses)
export const customToolDetailSchema = customToolSchema.extend({
  display_config: displayConfigSchema.nullable().optional(),
  whatsapp_template_name: z.string().nullable().optional(),
  token_auth: tokenAuthSchema.nullable().optional(),
});

export type CustomToolDetail = z.infer<typeof customToolDetailSchema>;

// Test custom tool request
export const testCustomToolSchema = z.object({
  http_method: httpMethodSchema,
  url: z.string().url('Invalid URL format'),
  headers: z.record(z.string(), z.string()).optional(),
  parameters_schema: parametersSchemaDefinition.optional(),
  output_path: z.string().optional(),
  selected_fields: z.array(z.string()).optional(),
});

export type TestCustomToolDto = z.infer<typeof testCustomToolSchema>;

export interface TestCustomToolResponse {
  success: boolean;
  raw_response: unknown;
  extracted_array: unknown[] | null;
  sample_fields: string[];
  error?: string;
  top_level_keys?: string[];
}
